import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  QUOTATION_TRANSITIONS,
  quotationsApi,
  type QuotationStatus
} from '@/api/modules/quotations';
import { ApiException } from '@/api/types';
import { paths } from '@/router/paths';

const STATUS_COLORS: Record<QuotationStatus, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  DRAFT: 'default',
  SENT: 'primary',
  ACCEPTED: 'success',
  REJECTED: 'error',
  EXPIRED: 'warning'
};

const TRANSITION_LABELS: Record<QuotationStatus, string> = {
  DRAFT: 'Move to draft',
  SENT: 'Send to customer',
  ACCEPTED: 'Mark accepted',
  REJECTED: 'Mark rejected',
  EXPIRED: 'Mark expired'
};

export function QuotationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['quotation', id],
    queryFn: () => quotationsApi.get(id!),
    enabled: Boolean(id)
  });

  const transition = useMutation({
    mutationFn: (next: QuotationStatus) => quotationsApi.transition(id!, next),
    onSuccess: (updated) => {
      queryClient.setQueryData(['quotation', id], updated);
      queryClient.invalidateQueries({ queryKey: ['quotations', 'summaries'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  });

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return (
      <Box>
        <BackToList />
        <Alert severity="error">
          Failed to load quotation:{' '}
          {query.error instanceof Error ? query.error.message : 'Not found'}
        </Alert>
      </Box>
    );
  }

  const q = query.data;
  const allowed = QUOTATION_TRANSITIONS[q.status];
  const transitionErr =
    transition.error instanceof ApiException
      ? transition.error.error.message
      : transition.error instanceof Error
        ? transition.error.message
        : null;

  return (
    <Box>
      <BackToList />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h1" gutterBottom>
            {q.code}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Created {formatDateTime(q.createdAt)}
          </Typography>
        </Box>
        <Chip label={q.status} color={STATUS_COLORS[q.status]} sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }} />
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            <DetailRow label="Customer">
              <Stack>
                <Typography>{q.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {q.customerCode}
                </Typography>
              </Stack>
            </DetailRow>
            <DetailRow label="Total">
              <Typography variant="h3">
                {q.currency} {q.totalAmount}
              </Typography>
            </DetailRow>
            <DetailRow label="Valid until">
              <Typography>{formatDate(q.validUntil)}</Typography>
            </DetailRow>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h2" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2 }}>
        Actions
      </Typography>

      {allowed.length === 0 ? (
        <Alert severity="info">
          This quotation is in a terminal state — no further transitions are allowed.
        </Alert>
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
              {allowed.map((next) => (
                <Button
                  key={next}
                  variant={next === allowed[0] ? 'contained' : 'outlined'}
                  color={next === 'REJECTED' || next === 'EXPIRED' ? 'inherit' : 'primary'}
                  onClick={() => transition.mutate(next)}
                  disabled={transition.isPending}
                >
                  {TRANSITION_LABELS[next]}
                </Button>
              ))}
            </Stack>
            {transitionErr && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {transitionErr}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {q.status === 'ACCEPTED' && (
        <Card variant="outlined" sx={{ mt: 2 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h3" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                  Convert to sales order
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prefills customer + total from this quotation.
                </Typography>
              </Box>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={() =>
                  navigate(`${paths.salesOrderCreate}?quotationId=${q.id}`)
                }
              >
                New sales order
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}

function BackToList() {
  const navigate = useNavigate();
  return (
    <Button
      startIcon={<ArrowBackIcon />}
      size="small"
      onClick={() => navigate(paths.quotations)}
      sx={{ mb: 2 }}
    >
      Back to quotations
    </Button>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Stack>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch { return iso; }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
}
